/**
 * The screen stays awake while live mode runs (J32; docs/plans/os-voice-conversation.md,
 * step 3.8): a screen wake lock (`navigator.wakeLock.request('screen')`)
 * while live mode runs and the tab is visible.
 *
 * The browser lets the lock go by itself when the tab hides, so it is asked
 * for again when the tab comes back to visible while live mode still runs. It
 * is let go on Stop live, Close and every ending (`stop`).
 *
 * A browser without `navigator.wakeLock`, a refused request (no user
 * activation, battery saver) or a `release` event changes nothing else: no
 * notice, no retry loop, and live mode carries on. The web has no switch for
 * it.
 */

/** What this file reads of the browser: the wake lock (absent where unsupported) and the page's visibility. */
export type WakeLockHost = {
  readonly wakeLock?: { readonly request: (type: 'screen') => Promise<WakeSentinel> } | undefined;
  readonly visible: () => boolean;
  readonly onVisibilityChange: (listener: () => void) => () => void;
};

/** A held lock (`WakeLockSentinel`): let go by `release`, and told by its `release` event when the browser lets it go. */
export type WakeSentinel = {
  readonly release: () => Promise<void>;
  readonly addEventListener?: (type: 'release', listener: () => void) => void;
};

export type LiveWakeLock = {
  /** Live mode started: hold the screen awake while the tab is visible. Idempotent. */
  readonly start: () => void;
  /** Live mode ended: let the screen sleep. Idempotent. */
  readonly stop: () => void;
};

/** The browser's own host, read when live mode starts. */
export const browserWakeLockHost = (): WakeLockHost => ({
  wakeLock: typeof navigator === 'undefined' || !('wakeLock' in navigator) ? undefined : (navigator.wakeLock as unknown as WakeLockHost['wakeLock']),
  visible: () => typeof document === 'undefined' || document.visibilityState === 'visible',
  onVisibilityChange: (listener) => {
    if (typeof document === 'undefined') return () => undefined;
    document.addEventListener('visibilitychange', listener);
    return () => document.removeEventListener('visibilitychange', listener);
  },
});

export const createLiveWakeLock = (host: () => WakeLockHost = browserWakeLockHost): LiveWakeLock => {
  let running = false;
  /** Which run a request belongs to: one that answers after its run ended lets its lock go at once. */
  let run = 0;
  let held: WakeSentinel | null = null;
  /** The run a request is out for, so a run asks once at a time, and a new run is never blocked by an old one's request. */
  let asking: number | null = null;
  let unwatch: (() => void) | null = null;

  const letGo = (sentinel: WakeSentinel): void => {
    void sentinel.release().catch(() => undefined);
  };

  const ask = (now: WakeLockHost): void => {
    const lock = now.wakeLock;
    if (!running || held !== null || asking === run || lock === undefined || !now.visible()) return;
    const mine = run;
    asking = mine;
    let pending: Promise<WakeSentinel>;
    try {
      pending = lock.request('screen');
    } catch {
      asking = null;
      return;
    }
    void pending.then(
      (sentinel) => {
        if (asking === mine) asking = null;
        if (!running || run !== mine) {
          letGo(sentinel);
          return;
        }
        held = sentinel;
        // The browser lets it go when the tab hides; the next visible asks again.
        sentinel.addEventListener?.('release', () => {
          if (held === sentinel) held = null;
        });
      },
      () => {
        // Refused: no notice and no retry; live mode carries on.
        if (asking === mine) asking = null;
      },
    );
  };

  return {
    start: () => {
      if (running) return;
      running = true;
      run += 1;
      const now = host();
      unwatch = now.onVisibilityChange(() => {
        if (now.visible()) {
          ask(now);
          return;
        }
        // Hidden: the browser lets the lock go; it is let go here too, so none is left behind.
        const sentinel = held;
        held = null;
        if (sentinel !== null) letGo(sentinel);
      });
      ask(now);
    },
    stop: () => {
      if (!running) return;
      running = false;
      run += 1;
      unwatch?.();
      unwatch = null;
      const sentinel = held;
      held = null;
      if (sentinel !== null) letGo(sentinel);
    },
  };
};
